import { XMarkIcon } from "@heroicons/react/24/outline";
import {
    ActionIcon,
    Badge,
    Box,
    Divider,
    Group,
    Loader,
    rem,
    Text,
} from "@mantine/core";
import { SVGProps } from "react";
import { FoundCountBadge } from "../../../components/common/FoundCountBadge";
import { AppliedFilterInfo } from "../../../models/person.model";
import { SearchMatchedBadges } from "./SearchMatchedBadges";

const IconXMark = (props: SVGProps<SVGSVGElement>) => <XMarkIcon {...props} />;

interface SearchResultsSummaryProps {
    totalItems: number;
    isFiltered: boolean;
    filteredCount: number;
    filterTitle: string;
    onClearFilter: () => void;
    appliedFilters: AppliedFilterInfo | null;
    loading: boolean;
    searchDuration: number;
    searchError: string | null;
}

export function SearchResultsSummary({
    totalItems,
    isFiltered,
    filteredCount,
    filterTitle,
    onClearFilter,
    appliedFilters,
    loading,
    searchDuration,
    searchError,
}: SearchResultsSummaryProps) {
    return (
        <Box style={{ width: '100%' }}>
            <Group gap="md" align="center" wrap="wrap" style={{ width: '100%' }}>
                <FoundCountBadge
                    count={isFiltered ? filteredCount : totalItems}
                    singularNoun="person"
                    pluralNoun="people"
                />

                <div
                    aria-live="polite"
                    aria-atomic="true"
                    style={{
                        position: "absolute",
                        left: "-10000px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                    }}
                >
                    {loading
                        ? "Searching for CVs..."
                        : `Found ${isFiltered ? filteredCount : totalItems
                        } ${isFiltered
                            ? filteredCount === 1
                                ? "person"
                                : "people"
                            : totalItems === 1
                                ? "person"
                                : "people"
                        }`}
                </div>

                {isFiltered && (
                    <>
                        <Divider orientation="vertical" />
                        <Badge color="blue" variant="light" size="lg">
                            Filtered: {filterTitle}
                        </Badge>
                        <ActionIcon
                            variant="subtle"
                            color="gray"
                            size="lg"
                            onClick={onClearFilter}
                            aria-label="Clear filter"
                        >
                            <IconXMark style={{ width: rem(16), height: rem(16) }} />
                        </ActionIcon>
                    </>
                )}

                {appliedFilters && (
                    <SearchMatchedBadges appliedFilters={appliedFilters} />
                )}
                {loading && (
                    <Group gap="xs">
                        <Loader size="xs" color="brand" />
                        <Text size="sm" fw={600} c="brand">
                            Searching...
                            {searchDuration > 0 && (
                                <Text component="span" size="xs" c="dimmed" ml="xs">
                                    ({searchDuration}s)
                                </Text>
                            )}
                        </Text>
                        {searchDuration > 30 && (
                            <Text size="xs" c="yellow" style={{ marginLeft: "8px" }}>
                                Large search in progress...
                            </Text>
                        )}
                        {searchDuration > 60 && (
                            <Text size="xs" c="orange" style={{ marginLeft: "8px" }}>
                                This may take several minutes.
                            </Text>
                        )}
                    </Group>
                )}
                {searchError && (
                    <Text size="sm" c="red" style={{ maxWidth: "300px" }}>
                        {searchError}
                    </Text>
                )}
            </Group>
        </Box>
    );
}

